import { format } from 'date-fns'
import { ESortValues, CARS_URL_DEFAULTS, URL_PARAM_TO_API_ID, type ICarsUrlState } from '../types/filters'

function parseCommaSeparated(value: string | string[] | undefined): string[] {
	if (!value) return []
	const raw = Array.isArray(value) ? value[0] : value
	if (!raw) return []
	return raw.split(',').filter(Boolean)
}

function parseNumber(value: string | string[] | undefined): number | null {
	if (!value) return null
	const raw = Array.isArray(value) ? value[0] : value
	const num = Number(raw)
	return Number.isFinite(num) ? num : null
}

function parseString(value: string | string[] | undefined): string | null {
	if (!value) return null
	const raw = Array.isArray(value) ? value[0] : value
	return raw || null
}

function isValidSort(value: string | undefined): value is ESortValues {
	return Object.values(ESortValues).includes(value as ESortValues)
}

export function useUrlFilters() {
	const route = useRoute()

	const state = computed<ICarsUrlState>(() => {
		const q = route.query
		return {
			groups: parseCommaSeparated(q.group as string),
			transmission: parseCommaSeparated(q.transmission as string),
			fuelType: parseCommaSeparated(q.fuel_type as string),
			seatsMin: parseNumber(q.seats_min as string),
			priceMin: parseNumber(q.price_min as string),
			priceMax: parseNumber(q.price_max as string),
			sort: isValidSort(q.sort as string) ? (q.sort as ESortValues) : null,
			page: parseNumber(q.page as string) ?? CARS_URL_DEFAULTS.page,
			limit: parseNumber(q.limit as string) ?? CARS_URL_DEFAULTS.limit,
			dateOut: parseString(q.date_out as string),
			dateIn: parseString(q.date_in as string),
			officeId: parseString(q.office_id as string),
		}
	})

	function buildQuery(updates: Partial<Record<string, string | undefined>>, resetPage = false): Record<string, string | undefined> {
		const current = { ...route.query } as Record<string, string | undefined>

		for (const [key, value] of Object.entries(updates)) {
			if (value === undefined || value === '') {
				delete current[key]
			} else {
				current[key] = value
			}
		}

		if (resetPage) {
			delete current.page
		}

		// Remove defaults to keep URL clean
		if (current.page === String(CARS_URL_DEFAULTS.page)) delete current.page
		if (current.limit === String(CARS_URL_DEFAULTS.limit)) delete current.limit

		return current
	}

	function updateUrl(query: Record<string, string | undefined>) {
		navigateTo({ query }, { replace: true })
	}

	function toggleArrayFilter(key: 'group' | 'transmission' | 'fuel_type', value: string) {
		const paramMap: Record<string, keyof ICarsUrlState> = {
			group: 'groups',
			transmission: 'transmission',
			fuel_type: 'fuelType',
		}
		const stateKey = paramMap[key]
		const current = [...(state.value[stateKey] as string[])]

		const index = current.indexOf(value)
		if (index >= 0) {
			current.splice(index, 1)
		} else {
			current.push(value)
		}

		const query = buildQuery({ [key]: current.length > 0 ? current.join(',') : undefined }, true)
		updateUrl(query)
	}

	function setSort(sort: ESortValues | null) {
		const query = buildQuery({ sort: sort ?? undefined })
		updateUrl(query)
	}

	function setPage(page: number) {
		const query = buildQuery({ page: page > 1 ? String(page) : undefined })
		updateUrl(query)
	}

	function setLimit(limit: number) {
		const query = buildQuery({ limit: limit !== CARS_URL_DEFAULTS.limit ? String(limit) : undefined }, true)
		updateUrl(query)
	}

	function setSeatsMin(min: number | null) {
		const query = buildQuery({ seats_min: min ? String(min) : undefined }, true)
		updateUrl(query)
	}

	function setDateAndLocation(dateOut: string | null, dateIn: string | null, officeId: string | null, preservePage = false) {
		const query = buildQuery(
			{
				date_out: dateOut ?? undefined,
				date_in: dateIn ?? undefined,
				office_id: officeId ?? undefined,
			},
			!preservePage,
		)
		updateUrl(query)
	}

	function setDatesOnly(dateOut: string | null, dateIn: string | null) {
		const query = buildQuery(
			{
				date_out: dateOut ?? undefined,
				date_in: dateIn ?? undefined,
			},
			true,
		)
		updateUrl(query)
	}

	function setOfficeOnly(officeId: string | null) {
		const query = buildQuery({ office_id: officeId ?? undefined }, true)
		updateUrl(query)
	}

	function setPriceRange(min: number | null, max: number | null) {
		const query = buildQuery(
			{
				price_min: min != null ? String(min) : undefined,
				price_max: max != null ? String(max) : undefined,
			},
			true,
		)
		updateUrl(query)
	}

	function clearFilters() {
		const query = buildQuery({
			group: undefined,
			transmission: undefined,
			fuel_type: undefined,
			seats_min: undefined,
			price_min: undefined,
			price_max: undefined,
			sort: undefined,
			page: undefined,
		})
		updateUrl(query)
	}

	function hasActiveFilters(): boolean {
		const s = state.value
		return (
			s.groups.length > 0 ||
			s.transmission.length > 0 ||
			s.fuelType.length > 0 ||
			s.seatsMin != null ||
			s.priceMin != null ||
			s.priceMax != null
		)
	}

	function toApiSearchParams(): IProductParameterSearch[] {
		const s = state.value
		const searches: IProductParameterSearch[] = []

		if (s.transmission.length > 0) {
			searches.push({ id: URL_PARAM_TO_API_ID.transmission, values: s.transmission })
		}

		if (s.fuelType.length > 0) {
			searches.push({ id: URL_PARAM_TO_API_ID.fuel_type, values: s.fuelType })
		}

		if (s.seatsMin != null) {
			searches.push({ id: URL_PARAM_TO_API_ID.seats_min, min: s.seatsMin })
		}

		if (s.groups.length > 0) {
			const carsStore = useCarsStore()
			const groupIds = s.groups.map((name) => carsStore.getAllGroups?.find((g) => g.name === name)?.id).filter((id): id is number => id != null)
			if (groupIds.length > 0) {
				searches.push({ id: URL_PARAM_TO_API_ID.group, values: groupIds })
			}
		}

		if (s.priceMin != null || s.priceMax != null) {
			searches.push({
				id: URL_PARAM_TO_API_ID.price,
				min: s.priceMin ?? undefined,
				max: s.priceMax ?? undefined,
			})
		}

		return searches
	}

	function toBusinessFilterValue(value: string): string {
		return format(new Date(value), 'dd-MM-yyyy HH:mm:ss')
	}

	function toApiFilterParams(defaults?: { dateOut?: string | null; dateIn?: string | null } | null): IProductFilter[] {
		const s = state.value
		const filters: IProductFilter[] = []

		const dateOut = s.dateOut ?? defaults?.dateOut
		const dateIn = s.dateIn ?? defaults?.dateIn

		if (dateOut) {
			filters.push({ id: 'date_out', value: toBusinessFilterValue(dateOut) })
		}

		if (dateIn) {
			filters.push({ id: 'date_in', value: toBusinessFilterValue(dateIn) })
		}

		if (s.officeId) {
			filters.push({ id: 'office_id', value: s.officeId })
		}

		filters.push({ id: 'has_price', value: '1' })

		return filters
	}

	return {
		state,
		toggleArrayFilter,
		setSort,
		setPage,
		setLimit,
		setSeatsMin,
		setPriceRange,
		setDateAndLocation,
		setDatesOnly,
		setOfficeOnly,
		clearFilters,
		hasActiveFilters,
		toApiSearchParams,
		toApiFilterParams,
	}
}
